import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Form,
  FormField,
  Heading,
  Label,
  Modal,
  Paragraph,
  ProgressBar,
} from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { RentalManagerUserTrackingEvent } from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useRentalManagerUserProfileWebApiHandler_saveUserInfo,
  useRentalManagerUserTrackingWebApiHandler_event,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { isEqual } from '@zg-rentals/zdash';

import typeCheckUtils from '../../../../srcLegacy/shared/utils/typeCheckUtils';
import { useValidateListing } from '../../../hooks';
import { useContracts, useCreateProperty } from '../../../hooks';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { listingErrors as listingErrorsReducer, listing as listingReducer } from '../AddPropertyModal/reducers';
import { useValidateUserProfile } from './hooks/useValidateUserProfile';
import { userProfileReducer } from './reducers';

import PropertyConstants from '../../../constants/PropertyConstants';
import {
  EMPTY_ERROR,
  EMPTY_LISTING,
  MAX_NUM_UNITS_ERROR,
  MAX_NUM_UNITS_VALUE,
  NUMBER_OF_UNITS_ERROR,
} from '../AddPropertyModal/constants';
import { EMPTY_USER_PROFILE, STEPS } from './constants';

import AddPropertyForm from '../AddPropertyModal/components/AddPropertyForm';
import ConfirmAddress from '../AddPropertyModal/components/ConfirmAddress';
import PersonalDetails from './components/PersonalDetails';
import PropertyCardPreview from './components/PropertyCardPreview';

import BackgroundImage from '../../../assets/onboarding_background.png';
import OnboardingUpsellCoffeeTable from '../../../assets/onboarding_upsell_coffee_table.png';
import { backgroundStyles } from './styles';

import {
  ADDRESS_ENTRY_EXIT_EVENT_ID,
  ADDRESS_ENTRY_GO_BACK_EVENT_ID,
  ADDRESS_ENTRY_NEXT_EVENT_ID,
  ADDRESS_VERIFICATION_EXIT_EVENT_ID,
  ADDRESS_VERIFICATION_GO_BACK_EVENT_ID,
  CREATE_LISTING_EVENT_ID,
  emitAddressEntryExitEvent,
  emitAddressEntryGoBackEvent,
  emitAddressEntryNextEvent,
  emitAddressVerificationExitEvent,
  emitAddressVerificationGoBackEvent,
  emitCreateListingEvent,
  emitProfileBuilderNextEvent,
  emitProfileBuilderSaveAndExitEvent,
  emitStartOnboardingEvent,
  PROFILE_BUILDER_NEXT_EVENT_ID,
  PROFILE_BUILDER_SAVE_AND_EXIT_EVENT_ID,
  START_ONBOARDING_EVENT_ID,
} from '../../../analytics/events/newUserOnboarding';
import { useAuth } from '../../AuthProvider';
import StaticMap from '../../StaticMap';

import type { ListingReducerProps } from '../AddPropertyModal/reducers';
import type { UserProps } from '../../../../srcLegacy/shared/model/User';
import type { StepType } from './types';

const {
  PROPERTY_TYPES: { COMMUNITY },
  LISTING_TYPES: { RENTAL, ROOM },
  ERROR_CODE: { IS_MULTI_FAMILY_RESTRICTED },
} = PropertyConstants;

const WizardUserOnboarding = ({ onClose, user: userProp }: { onClose: () => void; user?: UserProps }) => {
  const queryClient = useQueryClient();
  const { isScreenMobile: isMobile, isScreenXlGTE: shouldShowExtraVisual } = useScreenSize();
  const { mutateAsync: saveUserInfo } = useRentalManagerUserProfileWebApiHandler_saveUserInfo();
  const { mutate: trackEventMutation } = useRentalManagerUserTrackingWebApiHandler_event();
  const { createProperty: createPropertyMutation } = useCreateProperty();

  const { user: nextUser } = useAuth();
  const user = nextUser ?? userProp ?? null;

  const { firstName: existingFirstName = '', lastName: existingLastName = '' } = user || {};
  const isStaticMapAlreadyShowing = shouldShowExtraVisual;

  const [step, setStep] = useState<StepType>(STEPS.PERSONAL_DETAILS);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unitNumberDetected, setUnitNumberDetected] = useState(false);
  const [isValidatedWithoutUnitDetected, setIsValidatedWithoutUnitDetected] = useState(false);
  const [propertyAlreadyPostedWarningMessage, setPropertyAlreadyPostedWarningMessage] = useState<
    string | Array<JSX.Element | string>
  >('');
  const [isRefocusingAddressInput, setIsRefocusingAddressInput] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [isImpreciseLocation, setIsImpreciseLocation] = useState(false);

  const [listing, setListing] = useReducer(listingReducer, EMPTY_LISTING);
  const [errorObj, setErrorObj] = useReducer(listingErrorsReducer, EMPTY_ERROR);
  const emptyUserProfile = { ...EMPTY_USER_PROFILE, firstName: existingFirstName, lastName: existingLastName };
  const [userProfile, setUserProfile] = useReducer(userProfileReducer, emptyUserProfile);

  const { userProfileErrors } = useValidateUserProfile(userProfile, setIsSubmitDisabled, user);

  const contracts = useContracts(setListing, setErrorObj, setIsSubmitDisabled, user ?? undefined);

  const { verifyCanSubmitForm, handleCheckLocationRestriction } = useValidateListing(
    listing,
    setListing,
    errorObj,
    setErrorObj,
    setUnitNumberDetected,
    setIsValidatedWithoutUnitDetected,
    setPropertyAlreadyPostedWarningMessage,
    setIsImpreciseLocation,
    user ?? undefined,
    contracts,
  );

  useEffect(() => {
    emitStartOnboardingEvent({ user });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createProperty = async () => {
    const { isRoomForRent, propertyType, address, unit, numUnits, customerId, duplicateAccountListingAlias } = listing;
    const listingType = isRoomForRent ? ROOM : RENTAL;
    const isCommunityType = propertyType === COMMUNITY;

    // Duplicate listing exists. Redirect to existing listing rather than creating new one
    if (duplicateAccountListingAlias) {
      window.location.href = `/rental-manager/properties/${duplicateAccountListingAlias}/listing`;
    }

    if (!address || !propertyType) {
      return null;
    }

    const responseData = await createPropertyMutation({
      listingType,
      propertyType,
      fullAddress: address,
      ...(isCommunityType ? { numUnits } : { unitNumber: unit }),
      customerId: listingType === ROOM ? null : customerId,
    });

    return responseData;
  };

  const next = useCallback(() => {
    const didUpdateUserProfile = !isEqual(userProfile, emptyUserProfile);
    switch (step) {
      case STEPS.PERSONAL_DETAILS:
        if (didUpdateUserProfile) {
          saveUserInfo({
            getParams: {
              firstName: userProfile.firstName,
              lastName: userProfile.lastName,
              isPropertyManager: userProfile.isPropertyManager,
              isLandlord: userProfile.isLandlord,
              propertyCount: Number(userProfile.propertyCount) || 0,
            },
          })
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['modules', 'auth-react'] });
              trackEventMutation({
                getParams: { trackingEvent: RentalManagerUserTrackingEvent.COMPLETE_ON_BOARDING_FLOW },
              });
              emitProfileBuilderNextEvent({ user });
              return;
            })
            .then(() => {
              setStep(STEPS.ADD_PROPERTY);
              if (pageError) {
                setPageError(null);
              }
              return;
            })
            .catch(() => {
              setPageError('An error occurred while saving your user profile. Please try again.');
            });
        } else {
          trackEventMutation({
            getParams: { trackingEvent: RentalManagerUserTrackingEvent.COMPLETE_ON_BOARDING_FLOW },
          });
          emitProfileBuilderNextEvent({ user });

          setStep(STEPS.ADD_PROPERTY);
        }
        break;
      case STEPS.ADD_PROPERTY:
        setIsSubmitDisabled(true);
        verifyCanSubmitForm({ isPromptUnitNumberCheckRequired: !unitNumberDetected })
          .then((isValid) => {
            if (isValid) {
              emitAddressEntryNextEvent({ user });
              setStep(STEPS.CONFIRM_ADDRESS);
            }
          })
          .finally(() => {
            setIsSubmitDisabled(false);
          });

        if (isValidatedWithoutUnitDetected) {
          setIsValidatedWithoutUnitDetected(false);
        }

        if (unitNumberDetected && listing.unit) {
          setUnitNumberDetected(false);
        }

        if (propertyAlreadyPostedWarningMessage) {
          setPropertyAlreadyPostedWarningMessage('');
        }

        break;
      case STEPS.CONFIRM_ADDRESS:
        setIsLoading(true);
        setIsSubmitDisabled(true);
        createProperty()
          .then((responseData) => {
            if (!responseData) {
              return;
            }
            const { listingAlias } = responseData;
            emitCreateListingEvent({ user, listing: responseData });
            queryClient.invalidateQueries({ queryKey: ['getProperties'] });
            const isCommunityType = listing.propertyType === COMMUNITY;
            if (isCommunityType) {
              window.location.href = `/rental-manager/properties/${listingAlias}/listing`;
            } else {
              window.location.href = `/rental-manager/properties/${listingAlias}/dashboard/overview?showSuccessModal=true`;
            }
          })
          .catch((err) => {
            const isMultiFamilyRestricted = err.code === IS_MULTI_FAMILY_RESTRICTED;
            const fieldWithError = isMultiFamilyRestricted ? 'propertyType' : 'address';
            setErrorObj({ type: fieldWithError, payload: err.message });
            setStep(STEPS.ADD_PROPERTY);
          })
          .finally(() => {
            setIsLoading(false);
            setIsSubmitDisabled(false);
          });
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    listing,
    errorObj,
    unitNumberDetected,
    isValidatedWithoutUnitDetected,
    propertyAlreadyPostedWarningMessage,
    userProfile,
    pageError,
  ]);

  const back = useCallback(() => {
    switch (step) {
      case STEPS.ADD_PROPERTY:
        emitAddressEntryGoBackEvent({ user });
        setStep(STEPS.PERSONAL_DETAILS);
        break;
      case STEPS.CONFIRM_ADDRESS:
        emitAddressVerificationGoBackEvent({ user });
        setIsRefocusingAddressInput(true);
        setStep(STEPS.ADD_PROPERTY);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const close = useCallback(() => {
    switch (step) {
      case STEPS.PERSONAL_DETAILS:
        saveUserInfo({
          getParams: {
            firstName: userProfileErrors.firstName ? existingFirstName : userProfile.firstName,
            lastName: userProfileErrors.lastName ? existingLastName : userProfile.lastName,
            isPropertyManager: userProfile.isPropertyManager,
            isLandlord: userProfile.isLandlord,
            propertyCount: userProfileErrors.propertyCount ? 0 : Number(userProfile.propertyCount) || 0,
          },
        })
          .then(() => {
            trackEventMutation({
              getParams: { trackingEvent: RentalManagerUserTrackingEvent.COMPLETE_ON_BOARDING_FLOW },
            });
            emitProfileBuilderSaveAndExitEvent({ user });
          })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['modules', 'auth-react'] });
          });
        break;
      case STEPS.ADD_PROPERTY:
        emitAddressEntryExitEvent({ user });
        break;
      case STEPS.CONFIRM_ADDRESS:
        emitAddressVerificationExitEvent({ user });
        break;
      default:
        break;
    }
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, userProfile, userProfileErrors]);

  const handleListingChange = (keyName: keyof ListingReducerProps, newValue: string | boolean) => {
    // Convert boolean to string for legacy reducer
    const stringValue = typeof newValue === 'boolean' ? String(newValue) : newValue;

    const newListing = {
      ...listing,
      [keyName]: stringValue,
    };

    setErrorObj({ type: keyName, payload: '' });
    // if the unit was changed, the entire address will be revalidated
    keyName === 'unit' && setErrorObj({ type: 'address', payload: '' });

    if (
      newListing.numUnits &&
      (!typeCheckUtils.isNumeric(newListing.numUnits as string | number | undefined) ||
        String(newListing.numUnits).includes('.'))
    ) {
      setErrorObj({ type: 'numUnits', payload: NUMBER_OF_UNITS_ERROR });
    } else if (
      typeCheckUtils.isNumeric(newListing.numUnits as string | number | undefined) &&
      Number(newListing.numUnits) > MAX_NUM_UNITS_VALUE
    ) {
      setErrorObj({ type: 'numUnits', payload: MAX_NUM_UNITS_ERROR });
    }

    setListing({ type: keyName, payload: keyName === 'numUnits' ? stringValue?.trim() : stringValue });
  };

  const onChangePropertyType = (newPropertyType: string) => handleListingChange('propertyType', newPropertyType);

  const progressBar = (
    <FormField css={{ maxWidth: '300px' }}>
      <Label size="lg">Step {step} of 3</Label>
      <ProgressBar maxValue={3} value={step} size="md" />
    </FormField>
  );

  const form = (
    <>
      {pageError && <Alert tone="critical">{pageError}</Alert>}
      {step === STEPS.PERSONAL_DETAILS && (
        <PersonalDetails userProfile={userProfile} setUserProfile={setUserProfile} errors={userProfileErrors} />
      )}
      {step === STEPS.ADD_PROPERTY && (
        <>
          <Heading level={4} css={{ marginTop: { base: 'md', lg: 'xl' } }}>
            Now, let&apos;s add your property
          </Heading>
          <Paragraph css={{ marginTop: '400' }}>
            This basic information sets up the property in Zillow Rental Manager. Then you can create a rental listing
            for it.
          </Paragraph>
          <AddPropertyForm
            listing={{
              ...listing,
              isRoomForRent:
                typeof listing.isRoomForRent === 'boolean' ? listing.isRoomForRent : listing.isRoomForRent === 'true',
              isMultifamily:
                typeof listing.isMultifamily === 'boolean' ? listing.isMultifamily : listing.isMultifamily === 'true',
            }}
            contracts={contracts}
            errorObj={errorObj}
            isSubmitDisabled={isSubmitDisabled}
            setIsSubmitDisabled={setIsSubmitDisabled}
            unitNumberDetected={unitNumberDetected}
            isValidatedWithoutUnitDetected={isValidatedWithoutUnitDetected}
            propertyAlreadyPostedWarningMessage={propertyAlreadyPostedWarningMessage}
            onListingChange={handleListingChange}
            onCheckLocationRestriction={(keyName, newValue) =>
              handleCheckLocationRestriction(onChangePropertyType, keyName, newValue)
            }
            isRefocusingAddressInput={isRefocusingAddressInput}
            setIsRefocusingAddressInput={setIsRefocusingAddressInput}
          />
        </>
      )}
      {step === STEPS.CONFIRM_ADDRESS && (
        <>
          <Heading level={4} css={{ marginTop: { base: 'md', lg: 'xl' }, paddingBottom: { base: 'md', lg: 'lg' } }}>
            Is this information complete and correct?
          </Heading>

          <ConfirmAddress
            address={listing.address || ''}
            isRoomForRent={
              typeof listing.isRoomForRent === 'boolean' ? listing.isRoomForRent : listing.isRoomForRent === 'true'
            }
            numUnits={listing.numUnits}
            propertyType={listing.propertyType || ''}
            unit={listing.unit}
            shouldShowStaticMap={!isStaticMapAlreadyShowing}
            showImpreciseLocationAlert={isImpreciseLocation}
            showTermsInComponent={true}
          />
        </>
      )}
    </>
  );

  const supplementaryVisual = (
    <>
      {step === STEPS.PERSONAL_DETAILS && (
        <Box css={{ ...backgroundStyles, position: 'relative' }}>
          <img
            // @ts-expect-error - In Next.js, the image will have a src property
            src={typeof BackgroundImage === 'string' ? BackgroundImage : BackgroundImage.src}
            alt="blue background"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
          <Box css={{ position: 'relative', zIndex: 1 }}>
            <img
              src={
                typeof OnboardingUpsellCoffeeTable === 'string'
                  ? OnboardingUpsellCoffeeTable
                  : // @ts-expect-error - In Next.js, the image will have a src property
                    OnboardingUpsellCoffeeTable.src
              }
              alt="Create and sign leases, screen and message applicants, list your property for rent, and receive rent payments."
              style={{ width: 'max(80%, 500px)', height: 'auto', margin: '0 auto', display: 'block' }}
            />
          </Box>
        </Box>
      )}
      {step === STEPS.ADD_PROPERTY && <PropertyCardPreview address={listing.address} unit={listing.unit} />}
      {step === STEPS.CONFIRM_ADDRESS && (
        <Box css={{ ...backgroundStyles, position: 'relative' }}>
          <img
            // @ts-expect-error - In Next.js, the image will have a src property
            src={typeof BackgroundImage === 'string' ? BackgroundImage : BackgroundImage.src}
            alt="blue background"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
          <Box css={{ position: 'relative', zIndex: 1 }}>
            <StaticMap
              address={listing.address || ''}
              size="434x622"
              showImpreciseLocationAlert={isImpreciseLocation}
            />
          </Box>
        </Box>
      )}
    </>
  );

  const closeButtonDataEventId = useMemo(() => {
    if (step === STEPS.PERSONAL_DETAILS) {
      return PROFILE_BUILDER_SAVE_AND_EXIT_EVENT_ID;
    }
    if (step === STEPS.ADD_PROPERTY) {
      return ADDRESS_ENTRY_EXIT_EVENT_ID;
    }
    return ADDRESS_VERIFICATION_EXIT_EVENT_ID;
  }, [step]);

  const primaryButtonDataEventId = useMemo(() => {
    if (step === STEPS.PERSONAL_DETAILS) {
      return PROFILE_BUILDER_NEXT_EVENT_ID;
    }
    if (step === STEPS.ADD_PROPERTY) {
      return ADDRESS_ENTRY_NEXT_EVENT_ID;
    }
    if (step === STEPS.CONFIRM_ADDRESS) {
      return CREATE_LISTING_EVENT_ID;
    }
    return undefined;
  }, [step]);

  const backButtonDataEventId = useMemo(() => {
    if (step === STEPS.ADD_PROPERTY) {
      return ADDRESS_ENTRY_GO_BACK_EVENT_ID;
    }
    return ADDRESS_VERIFICATION_GO_BACK_EVENT_ID;
  }, [step]);

  return (
    <Modal open={true} onOpenChange={(open) => !open && close()} size="fullScreen" scrollDocument={true}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Heading level={1} data-eventid={START_ONBOARDING_EVENT_ID} data-eventdescription="View modal">
                Zillow Rental Manager
              </Heading>
              <Modal.Close data-testid="WizardUserOnboarding-Exit" data-eventid={closeButtonDataEventId}>
                <Button tone="brand" emphasis="outlined" size="md">
                  {step === STEPS.PERSONAL_DETAILS ? 'Save & exit' : 'Exit'}
                </Button>
              </Modal.Close>
            </Modal.Header>
            <Divider />
            <Modal.Body css={{ padding: '0 !important', display: 'flex' }}>
              {shouldShowExtraVisual ? (
                <Box css={{ display: 'flex', justifyContent: 'space-between', flexGrow: 1, alignSelf: 'stretch' }}>
                  <Box css={{ width: '60%', alignSelf: 'center' }}>
                    <Form asChild={true} css={{ maxWidth: '532px', margin: 'auto', padding: '400' }}>
                      <div>
                        {progressBar}
                        {form}
                      </div>
                    </Form>
                  </Box>
                  <Box
                    css={{
                      width: '40%',
                      padding: 0,
                      display: 'flex',
                      alignSelf: 'stretch',
                      flexDirection: 'column',
                    }}
                  >
                    {supplementaryVisual}
                  </Box>
                </Box>
              ) : (
                <Form asChild={true} css={{ width: '100%', padding: '400' }}>
                  <div>
                    {progressBar}
                    <Divider css={{ marginLeft: '-400', marginRight: '-400' }} />
                    {form}
                  </div>
                </Form>
              )}
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ButtonGroup
                aria-label="Form actions"
                css={{
                  width: '100%',
                  justifyContent: 'space-between',
                  flexDirection: 'row-reverse',
                }}
              >
                <Button
                  tone="brand"
                  emphasis="filled"
                  onClick={next}
                  disabled={isSubmitDisabled}
                  loading={isLoading}
                  css={{ minWidth: '164px' }}
                  data-eventid={primaryButtonDataEventId}
                >
                  {step === STEPS.CONFIRM_ADDRESS ? 'Confirm' : 'Next'}
                </Button>
                {step !== STEPS.PERSONAL_DETAILS && (
                  <Button
                    tone="brand"
                    emphasis="outlined"
                    onClick={back}
                    css={{ minWidth: '164px' }}
                    data-eventid={backButtonDataEventId}
                  >
                    {step === STEPS.CONFIRM_ADDRESS ? (isMobile ? 'Edit' : 'Edit property information') : 'Back'}
                  </Button>
                )}
              </ButtonGroup>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal>
  );
};

export default WizardUserOnboarding;
